import { ArrowsExpandIcon, MicrophoneIcon, PaperAirplaneIcon, PhoneMissedCallIcon, VideoCameraIcon } from '@heroicons/react/solid';
import { useState, useEffect, useRef } from 'react';
import { fetchAppointment } from '../providers/appointments';
import { peerConnectionConfig, signalingServer } from '../providers/webrtc';
import { useTranslations } from '../providers/i18n';

export default function VideoAppointment({ id, role }) {
    const [t, translationsLoaded] = useTranslations('booking-form');
    const [appointment, setAppointment] = useState(null);

    const localVideo = useRef(null);
    const remoteVideo = useRef(null);
    const connection = useRef(null);
    const socket = useRef(null);

    const [localStream, setLocalStream] = useState(null);
    const [remoteStream, setRemoteStream] = useState(null);

    const [camera, setCamera] = useState(true);
    const [microphone, setMicrophone] = useState(true);
    const [fullscreen, setFullscreen] = useState(false);
    const [callEnded, setCallEnded] = useState(false);

    const [newMessage, setNewMessage] = useState('');
    const [messages, setMessages] = useState([]);

    async function enableUserMedia() {
        if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: true,
                    audio: true,
                });
                localVideo.current.srcObject = stream;
                localVideo.current.play();
                setLocalStream(stream);
            } catch {
                alert('You must allow the usage of the camera and the microphone. Please do so and then refresh the page.');
            }
        } else {
            alert('Your browser does not support getUserMedia API');
            window.location.href = '/upcoming';
        }
    }

    async function connectWebSocket() {
        socket.current = new WebSocket(signalingServer);

        socket.current.onopen = () => {
            const message = {
                type: 'connect',
                appointment: appointment.id,
                role: role,
            };
            socket.current.send(JSON.stringify(message));
        };

        socket.current.onmessage = async (message) => {
            const { type, ...data } = JSON.parse(message.data);

            if (type == 'offer' && role == 'patient') {
                const { offer } = data;
                await connection.current.setRemoteDescription(new RTCSessionDescription(offer));
                const answer = await connection.current.createAnswer();
                await connection.current.setLocalDescription(answer);

                const message = {
                    type: 'answer',
                    appointment: appointment.id,
                    role: role,
                    answer: answer,
                };
                socket.current.send(JSON.stringify(message));
            }

            if (type == 'answer' && role == 'professional') {
                const { answer } = data;
                await connection.current.setRemoteDescription(new RTCSessionDescription(answer));
            }

            if (type == 'candidate') {
                const { candidate } = data;
                if (connection.current.remoteDescription) {
                    await connection.current.addIceCandidate(new RTCIceCandidate(candidate));
                }
            }

            if (type == 'start') {
                await connectCall();
            }

            if (type == 'stop') {
                await disconnectCall();
            }

            if (type == 'chat') {
                const { sender, body } = data;
                setMessages((messages) => [...messages, { sender, body }]);
            }
        };
    }

    async function connectCall() {
        connection.current = new RTCPeerConnection(peerConnectionConfig);

        connection.current.onicecandidate = (event) => {
            if (event.candidate) {
                const message = {
                    type: 'candidate',
                    appointment: appointment.id,
                    role: role,
                    candidate: event.candidate,
                };
                socket.current.send(JSON.stringify(message));
            }
        };

        if (role == 'professional') {
            connection.current.onnegotiationneeded = async () => {
                const offer = await connection.current.createOffer();
                await connection.current.setLocalDescription(offer);

                const message = {
                    type: 'offer',
                    appointment: appointment.id,
                    role: role,
                    offer: offer,
                };
                socket.current.send(JSON.stringify(message));
            };
        }

        connection.current.ontrack = (event) => {
            if (event.streams[0]) {
                setRemoteStream(event.streams[0]);
                remoteVideo.current.srcObject = event.streams[0];
            }
        };

        connection.current.onremovetrack = () => {
            if (remoteVideo.current.srcObject.getTracks() == 0) {
                remoteVideo.current.srcObject = null;
            }
        };

        localStream.getTracks().forEach((track) => {
            connection.current.addTrack(track, localStream);
        });
    }

    async function disconnectCall() {
        if (connection.current) {
            connection.current.close();
            connection.current = null;
            remoteVideo.current.srcObject = null;
            setRemoteStream(null);
        }
    }

    function toggleCamera() {
        const videoStreamTrack = localVideo.current.srcObject.getTracks().find(({ kind }) => kind == 'video');
        if (camera) {
            setCamera(false);
            videoStreamTrack.enabled = false;
        } else {
            setCamera(true);
            videoStreamTrack.enabled = true;
        }
    }

    function toggleMicrophone() {
        const audioStreamTrack = localVideo.current.srcObject.getTracks().find(({ kind }) => kind == 'audio');
        if (microphone) {
            setMicrophone(false);
            audioStreamTrack.enabled = false;
        } else {
            setMicrophone(true);
            audioStreamTrack.enabled = true;
        }
    }

    function toggleFullscreen() {
        setFullscreen(!fullscreen);
    }

    function endCall() {
        disconnectCall();
        localStream.getTracks().forEach((track) => {
            track.stop();
        });
        socket.current.close();
        setCallEnded(true);
    }

    function sendMessage(e) {
        e.preventDefault();
        if (socket.current) {
            const message = {
                type: 'chat',
                appointment: appointment.id,
                role: role,
                body: newMessage,
            };
            socket.current.send(JSON.stringify(message));
            setMessages([...messages, { sender: role, body: newMessage }]);
            setNewMessage('');
        }
    }

    useEffect(() => {
        fetchAppointment(id).then(setAppointment);
    }, []);

    useEffect(() => {
        if (appointment) {
            enableUserMedia();
        }
    }, [appointment]);

    useEffect(() => {
        if (localStream) {
            connectWebSocket();
        }
    }, [localStream]);

    if (!appointment) return <p>Loading...</p>;

    return (
        <div className="flex flex-1 flex-col gap-4 lg:flex-row">
            <div className={`overflow-hidden bg-black lg:basis-[800px] lg:self-start ${fullscreen ? 'fixed inset-0 z-30' : 'relative max-h-[600px] max-w-[800px] rounded-md shadow'}`}>
                <video ref={remoteVideo} className="h-full w-full object-contain" playsInline autoPlay />

                {!remoteStream && !callEnded && <div className="absolute inset-0 grid place-items-center text-white">Por favor aguarde...</div>}
                {callEnded && <div className="absolute inset-0 grid place-items-center text-white">Consulta concluída. Obrigado!</div>}

                {!callEnded && (
                    <div className="absolute right-4 bottom-4 max-h-[60px] max-w-[80px] overflow-hidden rounded-md sm:max-h-[120px] sm:max-w-[160px]">
                        <video ref={localVideo} className="h-full w-full object-contain" playsInline autoPlay muted />
                        {!camera && <div className="absolute inset-0 grid place-items-center bg-gray-800 text-white">Câmara desligada</div>}
                    </div>
                )}

                {!callEnded && (
                    <div className="absolute inset-x-0 bottom-6 flex items-center justify-center gap-6">
                        <div className={`cursor-pointer rounded-full p-2 sm:p-3 lg:p-4 ${camera ? 'bg-blue-700 text-white' : 'bg-gray-200 text-gray-500'}`} onClick={toggleCamera}>
                            <VideoCameraIcon className="h-4 sm:h-5 lg:h-6" />
                        </div>
                        <div className={`cursor-pointer rounded-full p-2 sm:p-3 lg:p-4 ${microphone ? 'bg-blue-700 text-white' : 'bg-gray-200 text-gray-500'}`} onClick={toggleMicrophone}>
                            <MicrophoneIcon className="h-4 sm:h-5 lg:h-6" />
                        </div>
                        <div className={`cursor-pointer rounded-full p-2 sm:p-3 lg:p-4 ${fullscreen ? 'bg-blue-700 text-white' : 'bg-gray-200 text-gray-500'}`} onClick={toggleFullscreen}>
                            <ArrowsExpandIcon className="h-4 sm:h-5 lg:h-6" />
                        </div>
                        <div className="cursor-pointer rounded-full bg-red-500 p-2 text-white sm:p-3 lg:p-4" onClick={endCall}>
                            <PhoneMissedCallIcon className="h-4 sm:h-5 lg:h-6" />
                        </div>
                    </div>
                )}
            </div>

            <div className="flex max-h-[600px] min-h-[300px] flex-col overflow-hidden rounded-md bg-white shadow-md lg:w-72 lg:flex-none">
                <div className="flex-none bg-blue-600 px-3 py-2 text-lg text-white shadow">Mensagens</div>
                <div className="flex flex-1 flex-col gap-1 overflow-auto p-3 text-sm">
                    {messages.map(({ sender, body }, index) => (
                        <div key={index} className={`rounded px-3 py-2 ${sender == role ? 'self-end bg-blue-600 text-white' : 'self-start bg-gray-100'}`}>
                            {body}
                        </div>
                    ))}
                </div>

                <form className="flex flex-none" onSubmit={sendMessage}>
                    <input
                        type="text"
                        className="flex-1 border-x-0 border-t border-b-0 border-gray-200 bg-gray-50 focus:border-gray-200 focus:ring-0"
                        value={newMessage}
                        onChange={({ target: { value } }) => setNewMessage(value)}
                        placeholder="Digite uma mensagem..."
                    />
                    <button className="flex-none bg-blue-600 px-3">
                        <PaperAirplaneIcon className="h-5 text-white" />
                    </button>
                </form>
            </div>
        </div>
    );
}
